// Module-level pub/sub store that tracks how many optimistic items are still
// pending (syncing) or failed (error) per editor instance, so sibling editors
// can be aggregated into one "N items not saved" indicator and used to gate
// Save buttons — without prop-drilling shared state through the tree.

// Keys are minted by useBackgroundSync and are unique per hook instance, NOT
// per (scope, period) — several editors can be mounted on the same meal period.
// Aggregation happens by `${scopeKey}:` prefix in usePendingSyncTotal.
const counts = new Map(); // `${scopeKey}:${periodName}#${instanceId}` -> { pending, error }
let snapshot = {};
const listeners = new Set();

function publish() {
  snapshot = Object.fromEntries(counts);
  listeners.forEach((l) => l());
}

export function subscribeSyncQueue(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSyncQueueSnapshot() {
  return snapshot;
}

// Publishes only on a real change. Editors report their counts on every mount
// and unmount, and most of those reports are no-ops (0/0 for a key that isn't
// tracked). Since publish() mints a new snapshot object, notifying on a no-op
// would hand useSyncExternalStore a fresh identity and re-render every
// subscriber — all of which are ancestors of the editors doing the reporting.
export function reportSyncCounts(periodKey, { pending, error }) {
  if (pending === 0 && error === 0) {
    if (!counts.delete(periodKey)) return;
  } else {
    const prev = counts.get(periodKey);
    if (prev && prev.pending === pending && prev.error === error) return;
    counts.set(periodKey, { pending, error });
  }
  publish();
}
